import NiceModal from '@ebay/nice-modal-react';

import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import EnumSelect from 'components/field/EnumSelect';
import SwitchInput from 'components/field/SwitchInput';
import FormItem from 'components/form/FormItem';
import Image from 'components/global/Image';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalContent } from 'components/global/styled/Modal.styled';
import { sideBarConfig } from 'components/layout/SideBar/SideBarConfig';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useChangeTheme from 'hooks/useChangeTheme';
import useLocalStorage from 'hooks/useLocalStorage';
import useMediaQuery from 'hooks/useMediaQuery';

import { Breakpoints } from 'utils/const.styles';

import { faText } from '@fortawesome/pro-solid-svg-icons';

import * as S from './LayoutPreference.styled';

const LayoutPreferenceModal = NiceModal.create(() => {
	const { showSuccess } = useAlert();
	const isAuthorized = useAccessControl();

	const [fontSize, setFontSize] = useLocalStorage('layoutScale', 7);
	const [saveDynamicListFilters, setSaveDynamicListFilters] = useLocalStorage('saveDynamicListFilters', true);
	const themeName = useSelector((state) => state.layout.themeName);

	const layoutScaleMap = useMemo(
		() => [
			{
				label: 'XX-Small',
				scale: 'XXS',
				value: 4
			},
			{
				label: 'X-Small',
				scale: 'XS',
				value: 5
			},
			{
				label: 'Small',
				scale: 'S',
				value: 6
			},
			{
				label: 'Medium',
				scale: 'M',
				value: 7
			},
			{
				label: 'Large',
				scale: 'L',
				value: 8
			},
			{
				label: 'X-Large',
				scale: 'XL',
				value: 9
			},
			{
				label: 'XX-Large',
				scale: 'XXL',
				value: 10
			}
		],
		[]
	);

	const visibleModeMap = useMemo(
		() => [
			{ label: 'systemTheme', img: 'system.png' },
			{ label: 'lightTheme', img: 'light.png' },
			{ label: 'darkTheme', img: 'dark.png' }
		],
		[]
	);

	const findFirstLink = (menuList) =>
		menuList.reduce((found, item) => {
			if (found) return found;
			if (item.links && item.links.length) {
				return findFirstLink(item.links);
			}
			return isAuthorized(item.path) ? item : null;
		}, null);

	const firstItem = findFirstLink(sideBarConfig);

	const user = useSelector((state) => state.user);

	const [defaultPage, setDefaultPage] = useLocalStorage(`${user.ID}_defaultPage`, firstItem);

	const findAuthorizedLinks = (menuList, arr) => {
		menuList.forEach((item) => {
			item.links && item.links.length
				? findAuthorizedLinks(item.links, arr)
				: isAuthorized(item.path) && arr.push(item);
		});
		return arr;
	};

	const authorizedLinks = findAuthorizedLinks(sideBarConfig, []);

	const defaultScale = layoutScaleMap.find(({ value }) => value === fontSize);
	const [selectedScale, setSelectedScale] = useState(defaultScale.scale);
	const changeTheme = useChangeTheme();

	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);

	const handleFontSizeChange = (value) => {
		setFontSize(value);
		if (isMobile) {
			document.getElementsByTagName('html')[0].style.fontSize = `${value + 2}px`;
		} else {
			document.getElementsByTagName('html')[0].style.fontSize = `calc(${value}px + 0.12vw)`;
		}
	};

	return (
		<Modal fullwidth maxWidth="medium" className="modal-layoutPreference">
			<ModalContent>
				<Typography
					fontWeight="500"
					style={{
						margin: '1rem 1rem 2rem 1rem'
					}}>
					layoutPreference
				</Typography>
				<FormItem label="layoutScale" childStyle={{ display: 'flex', flexDirection: 'row', gap: '2rem' }}>
					<S.GridScaleContainer>
						{layoutScaleMap.map(({ value, scale }, index) => (
							<S.ScaleContainer key={index}>
								<S.ItemButton
									variant="outline"
									selected={selectedScale === scale}
									icon={faText}
									iconSize={value / 2}
									height={11}
									onClick={() => {
										handleFontSizeChange(value);
										setSelectedScale(scale);
									}}
								/>
								<S.SlaceText fontWeight="500" selected={selectedScale === scale}>
									{scale}
								</S.SlaceText>
							</S.ScaleContainer>
						))}
					</S.GridScaleContainer>
				</FormItem>

				<FormItem
					label="theme"
					style={{ paddingTop: '2rem' }}
					childStyle={{ display: 'flex', flexDirection: 'row', gap: '2rem' }}>
					<S.GridThemeContainer>
						{visibleModeMap.map(({ label, img }) => (
							<S.ThemeContainer key={label}>
								<S.ItemButton
									variant="outline"
									selected={themeName === label}
									style={{ width: '18rem', padding: 0 }}
									height={13}
									onClick={() => changeTheme(label)}>
									<Image
										url={`https://storage.googleapis.com/tryoto-public/public/assets/images/${img}`}
										height="100%"
										width="unset"
									/>
								</S.ItemButton>

								<S.SlaceText fontWeight="500" selected={themeName === label}>
									{label}
								</S.SlaceText>
							</S.ThemeContainer>
						))}
					</S.GridThemeContainer>
				</FormItem>

				<FormItem label="defaultPage" style={{ paddingTop: '2rem' }}>
					<EnumSelect
						style={{ paddingTop: '0.7rem' }}
						description="defaultPageDescription"
						value={authorizedLinks.find((item) => item.title === defaultPage.title)?.title}
						setValue={(value) => {
							setDefaultPage(authorizedLinks.find((item) => item.title === value));
							showSuccess();
						}}
						data={authorizedLinks.map((item) => item.title)}
						valueRemovable={false}
					/>
				</FormItem>

				<FormItem label="saveDynamicListFilters" direction="row-reverse" style={{ paddingTop: '2rem' }}>
					<SwitchInput
						value={saveDynamicListFilters}
						setValue={(event) => {
							const value = event.target.checked;
							setSaveDynamicListFilters(value);
							showSuccess();
						}}
					/>
				</FormItem>
			</ModalContent>
		</Modal>
	);
});

export default LayoutPreferenceModal;
